import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, switchMap } from 'rxjs/operators';
import { openConfirmDialog } from 'app/components/common/confirm-dialog/confirm-dialog.component';
import { ViewSettingsDialogComponent, ViewSettingsDialogData, ViewSettingsDialogResult } from '../view-settings-dialog/view-settings-dialog.component';
import { GridStateCapture, TemplateViewService, isErrorRow } from 'app/services/template-view.service';
import { TemplateViewRow } from 'app/models/template-view';
import { TOAST_SHORT, TOAST_LONG, errorRowMessage } from '../manage-views.shared';

export interface ManageViewsDialogData {
  pageCode: string;
  activeTemplateViewID: number;
  /** The toolbar's already-loaded rows for this page — the dialog renders these
   *  instantly instead of refetching the full list (which ships every view's
   *  layout blobs and is slow). A silent background refresh reconciles. */
  views: TemplateViewRow[];
  captureState: () => GridStateCapture;
  applyView: (view: TemplateViewRow) => void;
  resetView: () => void;
}

/** Emitted only when something changed; a clean close emits undefined. */
export interface ManageViewsDialogResult {
  dirty: true;
  views: TemplateViewRow[];
  deletedIds: number[];
  newActiveId?: number;
}

type SortMode = 0 | 1 | 2; // 0 = server order, 1 = A→Z, 2 = Z→A

@Component({
  selector: 'app-manage-views-dialog',
  templateUrl: './manage-views-dialog.component.html',
  styleUrls: ['./manage-views-dialog.component.scss'],
  standalone: false,
})
export class ManageViewsDialogComponent implements OnInit {

  views: TemplateViewRow[] = [];
  myViews: TemplateViewRow[] = [];
  availableViews: TemplateViewRow[] = [];
  currentView: TemplateViewRow | null = null;
  isAdmin = false;
  mySort: SortMode = 0;
  availableSort: SortMode = 0;

  private dirty = false;
  private deletedIds: number[] = [];

  constructor(
    public dialogRef: MatDialogRef<ManageViewsDialogComponent, ManageViewsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ManageViewsDialogData,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private svc: TemplateViewService
  ) {}

  ngOnInit(): void {
    // The dialog is opened with disableClose so backdrop/ESC still route
    // through close() — the toolbar needs the result to merge local changes.
    this.dialogRef.backdropClick().subscribe(() => this.close());
    this.dialogRef.keydownEvents().subscribe(e => { if (e.key === 'Escape') this.close(); });

    this.isAdmin = this.svc.isTemplateAdmin();
    // Warm the user-directory cache now so the share picker opens instantly
    this.svc.prefetchDirectory();

    // Instant render from the toolbar's rows — no blocking fetch.
    this.views = (this.data.views || []).filter(v => (v.templateViewID ?? 0) > 0);
    this.adoptViews();

    // Silent background reconcile (catches changes made by OTHER users since
    // page load). Skipped if the user has already started mutating.
    this.refreshFromServer();
  }

  /** Replaces the local rows with fresh server rows for this page.
   *  No-ops once the user has local mutations (they are the newer truth) —
   *  pass force=true when the server rows are known to be newer. */
  private refreshFromServer(force = false) {
    this.svc.list().subscribe({
      next: rows => {
        if (this.dirty && !force) return;
        this.views = rows.filter(v =>
          v.pageCode === this.data.pageCode && (v.templateViewID ?? 0) > 0);
        this.adoptViews();
      },
      error: () => { /* keep rendering the local rows */ }
    });
  }

  private adoptViews() {
    if (this.views.length > 0 && this.views[0].isAdmin !== undefined) {
      this.isAdmin = this.views[0].isAdmin === 1;
    }
    this.currentView = this.views.find(v => v.templateViewID === this.data.activeTemplateViewID) || null;
    this.updateLists();
  }

  private updateLists() {
    this.myViews = this.applySort(this.views.filter(v => v.isOwner === 1), this.mySort);
    this.availableViews = this.applySort(this.views.filter(v => v.isOwner !== 1), this.availableSort);
  }

  /** Swap the authoritative row returned by a proc into the local list. */
  private applyRow(row: TemplateViewRow) {
    if (!row || (row.templateViewID ?? 0) <= 0) return;
    const idx = this.views.findIndex(v => v.templateViewID === row.templateViewID);
    if (idx > -1) this.views[idx] = row; else this.views.push(row);
    if (this.currentView?.templateViewID === row.templateViewID) this.currentView = row;
    this.dirty = true;
    this.updateLists();
  }

  private removeRow(templateViewID: number) {
    this.views = this.views.filter(v => v.templateViewID !== templateViewID);
    this.deletedIds.push(templateViewID);
    if (templateViewID === this.data.activeTemplateViewID) {
      this.clearActiveView();
    }
    this.dirty = true;
    this.updateLists();
  }

  /** The active view is gone (deleted or hidden): reset the grid + selection. */
  private clearActiveView() {
    this.data.resetView();
    this.data.activeTemplateViewID = 0;
    this.currentView = null;
  }

  private applySort(list: TemplateViewRow[], mode: SortMode): TemplateViewRow[] {
    if (mode === 0) return list;
    const sorted = [...list].sort((a, b) =>
      (a.templateViewName || '').localeCompare(b.templateViewName || ''));
    return mode === 1 ? sorted : sorted.reverse();
  }

  sortIcon(mode: SortMode): string {
    return mode === 0 ? 'swap_vert' : mode === 1 ? 'arrow_downward' : 'arrow_upward';
  }

  cycleMySort() { this.mySort = ((this.mySort + 1) % 3) as SortMode; this.updateLists(); }
  cycleAvailableSort() { this.availableSort = ((this.availableSort + 1) % 3) as SortMode; this.updateLists(); }

  get canEditCurrent(): boolean {
    return !!this.currentView && this.currentView.canEdit === 1;
  }

  isGlobal(v: TemplateViewRow): boolean { return this.svc.visibilityOf(v) === 'Global'; }

  showBadge(v: TemplateViewRow, ownSection: boolean): boolean {
    // The Global badge is hidden from non-admins in AVAILABLE VIEWS (per mock)
    return ownSection || !this.isGlobal(v) || this.isAdmin;
  }

  badgeClass(v: TemplateViewRow): string {
    return 'view-badge--' + this.svc.visibilityOf(v).toLowerCase();
  }

  visibilityLabel(v: TemplateViewRow): string { return this.svc.visibilityOf(v); }

  ownerLabel(v: TemplateViewRow): string { return v.isOwner === 1 ? 'You' : (v.ownerName || ''); }

  // ── Update current view ──────────────────────────────────────────────────

  updateCurrent() {
    if (!this.currentView) return;
    const view = this.currentView;
    const name = view.templateViewName;
    openConfirmDialog(this.dialog, {
      title: `Update “${name}”?`,
      message: "The view's saved layout will be replaced with the grid's current columns, filters, and sorting.",
      confirmText: 'Update'
    }).pipe(
      filter(Boolean),
      switchMap(() => this.svc.updateLayout(view.templateViewID, this.data.captureState()))
    ).subscribe({
      next: row => {
        if (isErrorRow(row)) {
          this.snackBar.open(errorRowMessage(row, "You don't have permission to change this view."),
            'Close', { duration: TOAST_LONG });
          return;
        }
        this.applyRow(row);
        this.snackBar.open(`“${name}” updated`, 'Close', { duration: TOAST_SHORT });
      },
      error: () => this.snackBar.open('Error updating the view. Please try again.', 'Close', { duration: TOAST_LONG })
    });
  }

  // ── Save as new view ─────────────────────────────────────────────────────

  saveAsNew() {
    this.openSettingsDialog({
      mode: 'new',
      pageCode: this.data.pageCode,
      captureState: this.data.captureState,
      isAdmin: this.isAdmin,
      existingNames: this.views.map(v => v.templateViewName)
    }).subscribe((res?: ViewSettingsDialogResult) => {
      if (res?.createdId) {
        const views = res.row ? [...this.views, res.row] : this.views;
        this.dialogRef.close({
          dirty: true,
          views,
          deletedIds: this.deletedIds,
          newActiveId: res.createdId
        });
      }
    });
  }

  // ── Row actions ──────────────────────────────────────────────────────────

  openSettings(view: TemplateViewRow) {
    this.openSettingsDialog({
      mode: 'edit',
      pageCode: this.data.pageCode,
      view,
      isAdmin: this.isAdmin,
      existingNames: this.views
        .filter(v => v.templateViewID !== view.templateViewID)
        .map(v => v.templateViewName)
    }).subscribe((res?: ViewSettingsDialogResult) => {
      if (!res) return;
      if (res.deleted) {
        this.removeRow(view.templateViewID);
        return;
      }
      if (res.changed) {
        if (res.row) {
          this.applyRow(res.row);
        } else {
          // Rare fallback (share-diff failure path without a row): forced
          // reconcile — the server rows are the newer truth here.
          this.dirty = true;
          this.refreshFromServer(true);
        }
      }
    });
  }

  toggleSubscription(view: TemplateViewRow, checked: boolean) {
    if (this.isGlobal(view)) return;
    const previous = view.subscribed;
    view.subscribed = checked ? 1 : 0;
    this.dirty = true;
    // Hiding the view currently applied to the grid also clears it — done
    // optimistically (like the toggle itself) so it never waits on the server
    // round-trip; the toolbar re-checks on close as a backstop.
    if (!checked && view.templateViewID === this.data.activeTemplateViewID) {
      this.clearActiveView();
    }
    this.svc.setSubscription(view.templateViewID, checked).subscribe({
      next: res => {
        if (isErrorRow(res)) {
          view.subscribed = previous;
          this.snackBar.open('Could not update the view list. Please try again.', 'Close', { duration: TOAST_LONG });
        }
      },
      error: () => {
        view.subscribed = previous;
        this.snackBar.open('Could not update the view list. Please try again.', 'Close', { duration: TOAST_LONG });
      }
    });
  }

  close() {
    // A clean close emits nothing — the toolbar has nothing to merge.
    this.dialogRef.close(this.dirty
      ? { dirty: true, views: this.views, deletedIds: this.deletedIds }
      : undefined);
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  private openSettingsDialog(data: ViewSettingsDialogData) {
    const config: MatDialogConfig = { width: '560px', maxHeight: '85vh', data };
    return this.dialog.open(ViewSettingsDialogComponent, config).afterClosed();
  }
}
